import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { cloneDeep } from 'lodash';
import PropTypes from 'prop-types';
import { nanoid } from 'nanoid';

import {
  CONCEPT_IMAGE_SURVEY_TYPES,
  DEFAULT_GROUP_NAME,
  ERROR_SAVE_DRAFT,
  readInstantSurvey,
  RESPONSE_COUNT_INCREMENT,
  SURVEY_GROUP_VALIDATION_STATUS,
  SURVEY_TYPES,
  validateInstantSurvey,
  validateInstantSurveyPrompts,
  VIDEO_MULTI_GROUP_RESPONSE_COUNT,
  VIDEO_RESPONSE_COUNT_INCREMENT,
  VIDEO_SINGLE_GROUP_RESPONSE_COUNT,
} from '@api/instant_survey';

import { ActionArea, Button, CheckCircleIcon, OverlayTriggerTooltip, toast } from '@utilities';
import { PlusIcon } from '@utilities/icons';

import { CONCEPT_ERROR_STATE, CONCEPT_ERROR_MESSAGES } from './utilities/constants';
import ModalDeleteQuotaGroup from './components/ModalDeleteQuotaGroup';
import ModalDuplicateGroup from './components/ModalDuplicateGroup';
import ModalMissingRequiredPrompts from './components/ModalMissingRequiredPrompts';
import ModalValidationErrors from './components/ModalValidationErrors';
import QuotaGroupCard from './components/QuotaGroupCard';
import SurveyToolbar from '../SurveyToolbar';

import { SurveyContext } from '../../utilities/context';

import styles from './_index.module.scss';

const Panelists = ({ defaultResponseCount, onNext }) => {
  const {
    activeGroups,
    insightsDocumentType,
    instantSurveyType,
    isReadOnly,
    isResearchObjectivesTooLong,
    persistSurveyDraft,
    survey,
    updateSurvey,
  } = useContext(SurveyContext);

  const [groupToDelete, setGroupToDelete] = useState(null);
  const [groupToDuplicate, setGroupToDuplicate] = useState(null);
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [isValidatingPanelists, setIsValidatingPanelists] = useState(false);
  const [isValidatingResponses, setIsValidatingResponses] = useState(false);
  const [validatingSurveyId, setValidatingSurveyId] = useState(null);
  const [validationErrors, setValidationErrors] = useState(null);
  const [visibleBehaviorsByGroup, setVisibleBehaviorsByGroup] = useState(new Map());
  const [visiblePromptsByGroup, setVisiblePromptsByGroup] = useState(new Map());

  const { NOT_VALIDATED, VALIDATING } = SURVEY_GROUP_VALIDATION_STATUS;

  const surveysResponsesValidation = useSelector((state) => state?.websocket?.surveyValidation);

  const isConceptImageTypeTest = CONCEPT_IMAGE_SURVEY_TYPES.includes(instantSurveyType?.id);
  const isVideoSurvey = instantSurveyType?.id === SURVEY_TYPES.VIDEO;

  const controller = new AbortController();

  const responseCountIncrement = isVideoSurvey
    ? VIDEO_RESPONSE_COUNT_INCREMENT
    : RESPONSE_COUNT_INCREMENT;
  const signal = controller.signal;
  const surveyIdRef = useRef(null);

  const getGroupKey = useCallback((group) => group?.clientId || group?.id, []);

  const getVisibleBehaviorCount = useCallback(
    (groupKey) => visibleBehaviorsByGroup.get(groupKey),
    [visibleBehaviorsByGroup]
  );

  const setVisibleBehaviorCount = useCallback((groupKey, count) => {
    setVisibleBehaviorsByGroup((prev) => {
      const current = prev.get(groupKey);

      if (current === count) {
        return prev;
      }

      const next = new Map(prev);
      next.set(groupKey, count);
      return next;
    });
  }, []);

  const clearGroupState = useCallback(
    (group) => {
      const key = getGroupKey(group);
      if (!key) return;
      setVisiblePromptsByGroup((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
      setVisibleBehaviorsByGroup((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
    },
    [getGroupKey]
  );

  const updateVisiblePrompts = useCallback((groupKey, updater) => {
    setVisiblePromptsByGroup((prev) => {
      const prevSet = prev.get(groupKey) || new Set();
      const nextSet = updater(prevSet);

      const isSameSet =
        prevSet.size === nextSet.size && [...prevSet].every((id) => nextSet.has(id));

      if (isSameSet) {
        return prev;
      }

      const next = new Map(prev);
      next.set(groupKey, nextSet);
      return next;
    });
  }, []);

  const currentTotalResponses = activeGroups.reduce((acc, group) => acc + group.quotaLimit, 0);
  const minimumResponsesRequired = Math.min(
    ...insightsDocumentType.samplePricing
      .filter(({ quotaGroups }) => quotaGroups === activeGroups.length)
      .map(({ responses }) => responses)
  );

  const canAddQuotaGroup = insightsDocumentType.samplePricing.find(
    ({ quotaGroups }) => quotaGroups === activeGroups.length + 1
  );
  const canDecrementResponses = insightsDocumentType.samplePricing.some(
    ({ responses }) => responses === currentTotalResponses - responseCountIncrement
  );
  const canIncrementResponses =
    currentTotalResponses <
    Math.max(
      ...insightsDocumentType.samplePricing
        .filter(({ quotaGroups }) => quotaGroups === activeGroups.length)
        .map(({ responses }) => responses)
    );

  const conceptErrorState = useMemo(() => {
    if (!isConceptImageTypeTest) return null;

    const groupStates = activeGroups.map(({ conceptImage, conceptName }) => ({
      hasImage: Boolean(conceptImage),
      hasName: Boolean(conceptName),
    }));

    const someMissingBoth = groupStates.some(({ hasImage, hasName }) => !hasImage && !hasName);
    const someHaveImage = groupStates.some(({ hasImage }) => hasImage);
    const allHaveImage = groupStates.every(({ hasImage }) => hasImage);
    const someHaveName = groupStates.some(({ hasName }) => hasName);
    const allHaveName = groupStates.every(({ hasName }) => hasName);

    if (someMissingBoth) return CONCEPT_ERROR_STATE.MISSING_EITHER;
    if (someHaveImage && !allHaveImage) return CONCEPT_ERROR_STATE.INCONSISTENT_IMAGE;
    if (someHaveName && !allHaveName) return CONCEPT_ERROR_STATE.INCONSISTENT_NAME;

    return null;
  }, [isConceptImageTypeTest, activeGroups]);

  const conceptErrorMessage = conceptErrorState ? CONCEPT_ERROR_MESSAGES[conceptErrorState] : null;

  const groupsMissingRequiredPrompts = activeGroups
    .map(({ clientId, id, name, promptAnswers }) => ({
      clientId,
      id,
      name,
      prompts: instantSurveyType.prompts.filter((prompt) => {
        const groupKey = getGroupKey({ clientId, id });
        const visiblePromptIds = visiblePromptsByGroup.get(groupKey) || new Set();
        if (!visiblePromptIds.has(prompt.id)) return false;
        if (!prompt.isRequired) return false;

        const answers = promptAnswers.filter(
          ({ _delete, promptId }) => !_delete && prompt.id === promptId
        );
        return answers.length === 0 || answers.some(({ values }) => values.length === 0);
      }),
    }))
    .filter(({ prompts }) => prompts.length > 0);
  const remainingAvailableResponses =
    Math.max(
      ...insightsDocumentType.samplePricing
        .filter(({ quotaGroups }) => quotaGroups === activeGroups.length + 1)
        .map(({ responses }) => responses)
    ) - currentTotalResponses;

  const addQuotaGroupTooltip =
    canAddQuotaGroup && canIncrementResponses
      ? 'Add Quota Group'
      : !canAddQuotaGroup
        ? 'Maximum amount of quota groups reached'
        : 'Maximum amount of responses reached: you cannot create another quota group';

  const isContinueDisabled =
    currentTotalResponses < minimumResponsesRequired ||
    activeGroups.some(({ name }) => !name.trim()) ||
    new Set(activeGroups.map(({ name }) => name.trim())).size !== activeGroups.length ||
    isResearchObjectivesTooLong;
  const navigate = useNavigate();

  const onAddQuotaGroup = () => {
    updateSurvey(
      {
        groups: [
          ...survey.groups,
          {
            clientId: nanoid(),
            genRepSampleType: null,
            id: null,
            name: `${DEFAULT_GROUP_NAME} (${activeGroups.length + 1})`,
            promptAnswers: [],
            quotaLimit: Math.min(defaultResponseCount, remainingAvailableResponses),
          },
        ],
      },
      { markDirty: true }
    );
  };

  const saveAndValidateSurveyDraft = async () => {
    if (groupsMissingRequiredPrompts.length > 0 || conceptErrorMessage) {
      setIsValidationModalOpen(true);
      return null;
    }

    const survey = await handleSaveSurvey();
    if (!survey) return null;

    const isValid = await handleValidateSurveyPrompts(survey.id);
    return isValid ? survey : null;
  };

  const handleSaveSurvey = async () => {
    try {
      const persistResult = await persistSurveyDraft({ reason: 'panelists-continue' });
      return persistResult?.instantSurveyResponse || null;
    } catch (error) {
      console.error('Error saving survey draft:', error);
      toast(ERROR_SAVE_DRAFT, { status: 'error' });
      return null;
    }
  };

  const handleValidateSurveyPrompts = async (id) => {
    try {
      await validateInstantSurveyPrompts({ id });
      return true;
    } catch (error) {
      const validationErrors = error?.response?.data?.error || [];
      setValidationErrors(validationErrors);
      return false;
    }
  };

  const onContinue = async () => {
    if (isReadOnly) {
      onNext();
      return;
    }

    setIsValidatingPanelists(true);
    try {
      const survey = await saveAndValidateSurveyDraft();
      if (survey) {
        onNext();
      }
    } catch (error) {
      console.error('Unexpected error during survey continuation:', error);
    } finally {
      setIsValidatingPanelists(false);
    }
  };

  const normalizeVideoQuotaLimits = useCallback(
    (groups) => {
      if (!isVideoSurvey) return groups;

      const activeSurveyGroups = groups.filter(({ _delete }) => !_delete);
      const minimumGroupResponses =
        activeSurveyGroups.length > 1
          ? VIDEO_MULTI_GROUP_RESPONSE_COUNT
          : VIDEO_SINGLE_GROUP_RESPONSE_COUNT;

      return groups.map((group) => {
        if (group._delete) return group;

        const normalizedQuotaLimit = Math.max(minimumGroupResponses, group.quotaLimit);

        if (normalizedQuotaLimit === group.quotaLimit) return group;

        return {
          ...group,
          quotaLimit: normalizedQuotaLimit,
        };
      });
    },
    [isVideoSurvey]
  );

  const onDelete = (group) => {
    if (!group.id) {
      const updatedGroups = normalizeVideoQuotaLimits(
        survey.groups.filter(({ clientId }) => clientId !== group.clientId)
      );

      updateSurvey({ groups: updatedGroups }, { markDirty: true });
      clearGroupState(group);
      return;
    }

    const affectedQuestions = [];
    const persistedGroupId = group.id;
    for (const [questionId, logicRow] of Object.entries(survey.questionLogic)) {
      if (logicRow.skip?.logic) {
        for (const logic of logicRow.skip.logic) {
          if (
            persistedGroupId != null &&
            logic.url_variable &&
            logic.values.includes(String(persistedGroupId))
          ) {
            const question = survey.questions.find(({ id }) => id === parseInt(questionId));
            affectedQuestions.push({
              id: question.id,
              text: question.text,
            });
            break;
          }
        }
      }
    }

    if (affectedQuestions.length > 0) {
      setGroupToDelete({
        affectedQuestions: affectedQuestions,
        clientId: group.clientId,
        id: group.id,
        name: group.name,
      });
    } else {
      onUpdateGroup(group, { _delete: true });
      clearGroupState(group);
    }
  };

  const onDuplicateGroup = ({ genRepSampleType, name, promptAnswers = [], quotaLimit }) => {
    const duplicatedPromptAnswers = promptAnswers.map((answer) => {
      if (!answer) return answer;

      const cloned = cloneDeep(answer);
      delete cloned.id;
      delete cloned._delete;

      return cloned;
    });

    setGroupToDuplicate({
      clientId: nanoid(),
      genRepSampleType,
      id: null,
      name,
      promptAnswers: duplicatedPromptAnswers,
      quotaLimit,
    });
  };

  const onUpdateGroup = (groupRef, propsToUpdate) => {
    const groupIndex = survey.groups.findIndex(({ clientId, id }) => {
      if (groupRef?.clientId) return clientId === groupRef.clientId;
      if (groupRef?.id !== undefined && groupRef?.id !== null) return id === groupRef.id;
      return id === groupRef;
    });
    if (groupIndex === -1) return;

    const updatedGroups = [...survey.groups];
    const updates = { ...propsToUpdate };
    if (updates.answers) updates.validationStatus = null;
    updatedGroups[groupIndex] = Object.assign(updatedGroups[groupIndex], updates);
    updateSurvey({ groups: normalizeVideoQuotaLimits(updatedGroups) }, { markDirty: true });
  };

  const onValidateResponses = async () => {
    setIsValidatingResponses(true);

    try {
      const survey = await saveAndValidateSurveyDraft();
      if (!survey) return setIsValidatingResponses(false);

      const hasGroupsToValidate = survey.groups?.some(({ validationStatus }) =>
        [NOT_VALIDATED, VALIDATING].includes(validationStatus)
      );

      if (!hasGroupsToValidate) return setIsValidatingResponses(false);

      await validateInstantSurvey({ id: survey.id, signal });

      setValidatingSurveyId(survey.id);
      surveyIdRef.current = survey.id;
    } catch (error) {
      console.error('Error validating responses:', error);
      toast('Validation failed', { status: 'error' });
      setIsValidatingResponses(false);
    }
  };

  useEffect(() => {
    if (!validatingSurveyId) return;

    const surveyValidation = surveysResponsesValidation?.find(
      ({ id }) => id === surveyIdRef.current
    );

    const validStatuses = ['COMPLETED', 'FAILED'];

    if (!validStatuses.includes(surveyValidation?.status)) return;

    const fetchSurveyValidation = async () => {
      try {
        const { groups } = await readInstantSurvey({ id: surveyIdRef.current });

        updateSurvey({ groups }, { markDirty: false });
      } catch (error) {
        console.error('Error reading survey:', error);
      } finally {
        setValidatingSurveyId(null);
        surveyIdRef.current = null;
        setIsValidatingResponses(false);
      }
    };

    fetchSurveyValidation();
  }, [surveysResponsesValidation]);

  return (
    <div className={styles['panelists']}>
      <ActionArea
        buttons={[
          <Button
            data-testid="cancel-button"
            key="cancel-button"
            onClick={() => navigate(-1)}
            text="Cancel"
            variant="secondary"
          />,
          <Button
            data-testid="continue-button"
            isDisabled={isContinueDisabled}
            isLoading={isValidatingPanelists}
            key="continue-button"
            onClick={onContinue}
            text="Continue"
          />,
        ]}
        className={styles['panelists-layout']}
      >
        <SurveyToolbar
          isSaveAsDraftDisabled={isContinueDisabled}
          tools={
            isReadOnly
              ? []
              : [
                  <OverlayTriggerTooltip
                    content={isValidatingResponses ? 'Validating Sample...' : 'Validate Sample'}
                    key="validate-responses"
                  >
                    <button disabled={isValidatingResponses} onClick={onValidateResponses}>
                      <CheckCircleIcon />
                    </button>
                  </OverlayTriggerTooltip>,
                  <OverlayTriggerTooltip content={addQuotaGroupTooltip} key="add-quota-group">
                    <button
                      disabled={!canAddQuotaGroup || !canIncrementResponses}
                      onClick={onAddQuotaGroup}
                    >
                      <PlusIcon />
                    </button>
                  </OverlayTriggerTooltip>,
                ]
          }
        />
        {currentTotalResponses < minimumResponsesRequired && (
          <div className={styles['panelists-pricing-error']}>
            An Instant Survey must have a minimum of {minimumResponsesRequired} responses.
          </div>
        )}
        <ul className={styles['panelists-cards']}>
          {activeGroups.map((group) => (
            <li key={group.clientId || group.id}>
              <QuotaGroupCard
                canDecrementResponses={canDecrementResponses}
                canDelete={activeGroups.length > 1}
                canDuplicate={canAddQuotaGroup && canIncrementResponses}
                canIncrementResponses={canIncrementResponses}
                getVisibleBehaviorCount={getVisibleBehaviorCount}
                group={group}
                groupToDelete={groupToDelete}
                isValidatingResponses={isValidatingResponses}
                isVideoSurvey={isVideoSurvey}
                onDelete={() => onDelete(group)}
                onDuplicate={() => onDuplicateGroup(group)}
                onUpdate={(propsToUpdate) => onUpdateGroup(group, propsToUpdate)}
                setVisibleBehaviorCount={setVisibleBehaviorCount}
                survey={survey}
                surveyType={instantSurveyType}
                updateVisiblePrompts={updateVisiblePrompts}
              />
            </li>
          ))}
        </ul>
        {!isReadOnly && (
          <div className={styles['panelists-add-quota-group']}>
            <OverlayTriggerTooltip content={addQuotaGroupTooltip} position="bottom">
              <button
                data-testid="add-quota-group-button"
                disabled={!canAddQuotaGroup || !canIncrementResponses}
                onClick={onAddQuotaGroup}
              >
                <PlusIcon />
                <span>
                  Add another <strong>Quota Group</strong>
                </span>
              </button>
            </OverlayTriggerTooltip>
          </div>
        )}
      </ActionArea>
      {groupToDelete && (
        <ModalDeleteQuotaGroup
          group={groupToDelete}
          onClose={() => setGroupToDelete(null)}
          onSubmit={() => {
            onUpdateGroup(groupToDelete, { _delete: true });
            clearGroupState(groupToDelete);
            setGroupToDelete(null);
          }}
        />
      )}
      {groupToDuplicate && (
        <ModalDuplicateGroup
          group={groupToDuplicate}
          groups={activeGroups}
          onClose={() => setGroupToDuplicate(null)}
          onSubmit={(group) => {
            updateSurvey({ groups: [...survey.groups, group] }, { markDirty: true });
            setGroupToDuplicate(null);
          }}
          remainingAvailableResponses={remainingAvailableResponses}
        />
      )}
      {isValidationModalOpen && (
        <ModalMissingRequiredPrompts
          conceptErrorMessage={conceptErrorMessage}
          groups={activeGroups}
          invalidGroups={groupsMissingRequiredPrompts}
          onClose={() => setIsValidationModalOpen(false)}
        />
      )}
      {validationErrors && (
        <ModalValidationErrors
          onClose={() => setValidationErrors(null)}
          survey={survey}
          validationErrors={validationErrors}
        />
      )}
    </div>
  );
};

Panelists.propTypes = {
  defaultResponseCount: PropTypes.number.isRequired,
  onNext: PropTypes.func.isRequired,
};

export default Panelists;
